import type {
  INotificationConfigRepository,
  NotificationConfig,
  NotificationType,
} from '@yawi/domain';
import { CATEGORY_TO_TYPES } from '@yawi/domain';

const ALL_TYPES: NotificationType[] = Object.values(CATEGORY_TO_TYPES).flat() as NotificationType[];

const buildDefaults = (): Map<NotificationType, NotificationConfig> => {
  const map = new Map<NotificationType, NotificationConfig>();
  for (const type of ALL_TYPES) {
    map.set(type, {
      type,
      enabled: true,
      channels: ['PUSH', 'IN_APP'],
      cadence: { mode: 'immediate' },
      priority: 'NORMAL',
      quietHours: null,
      updatedAt: new Date().toISOString(),
    });
  }
  return map;
};

export class MockNotificationConfigRepository implements INotificationConfigRepository {
  private store: Map<NotificationType, NotificationConfig> = buildDefaults();

  async list(): Promise<NotificationConfig[]> {
    return Array.from(this.store.values());
  }

  async getByType(type: NotificationType): Promise<NotificationConfig | null> {
    return this.store.get(type) ?? null;
  }

  async update(
    type: NotificationType,
    patch: Partial<Omit<NotificationConfig, 'type' | 'updatedAt'>>
  ): Promise<NotificationConfig> {
    const current = this.store.get(type);
    if (!current) throw new Error(`NotificationType not found: ${type}`);
    const updated: NotificationConfig = { ...current, ...patch, type, updatedAt: new Date().toISOString() };
    this.store.set(type, updated);
    return updated;
  }

  async bulkUpdate(
    updates: Array<{ type: NotificationType } & Partial<Omit<NotificationConfig, 'updatedAt'>>>
  ): Promise<NotificationConfig[]> {
    const results: NotificationConfig[] = [];
    for (const { type, ...patch } of updates) {
      results.push(await this.update(type, patch));
    }
    return results;
  }

  async resetToDefaults(): Promise<NotificationConfig[]> {
    this.store = buildDefaults();
    return Array.from(this.store.values());
  }
}
